import React, { useEffect, useRef, useState } from 'react'

import useEnrollmentApi from '@hooks/useEnrollmentApi'

import PageContent from '@components/layout/PageContent'
import { Alert, Text, Button, Flex } from '@clear/design-system'
import { Stepper } from '@components/storybook'
import { StepTitle } from '@components/layout/Form'
import { enrollSteps } from '@utils/steps'
import useLookupData, { KEYS } from '@hooks/useLookupData'
import Spinner from '@components/shared/Spinner'
import OptInModal from '@components/shared/OptInModal'
import { ageConfirmed } from '@utils/dateDifference'
import UserDataTracker from '@components/shared/UserDataTracker'
import { useRouter } from 'next/router'
import { LocationWithLanes } from '@clear-denver/tpc-bff-client/build/api/tpc-bff/types'
import { FeatureCard } from '@components/storybook/FeatureCard'
import { useLocationsContext } from '@contexts/LocationsContext'
export let preloadedFlowContainer = null

type ConfirmationData = {
  enrollmentId: string
  applicationId: string
  documentAcronym: string
  photoIdAcronym: string
  enrollmentLocationId: string
  namesOnDocumentsMatch: boolean | null
  email: string | null
  dateOfBirth: Date | null
  serviceTerritoryId?: string
  firstName: string
  middleName?: string
  lastName: string
}

const defaultConfirmationData: ConfirmationData = {
  enrollmentId: '',
  applicationId: '',
  documentAcronym: '',
  photoIdAcronym: '',
  enrollmentLocationId: '',
  namesOnDocumentsMatch: null,
  email: null,
  dateOfBirth: null,
  serviceTerritoryId: null,
  firstName: '',
  middleName: '',
  lastName: '',
}

const CompletePage: React.FC = () => {
  const { state, clearState } = useEnrollmentApi()
  const { enrollmentLocations } = useLocationsContext()
  const [isLoading, setIsLoading] = useState(false)
  const [chosenLocation, setChosenLocation] = useState(null)
  const [isFlowLoaded, setIsFlowLoaded] = useState(false)
  const [confirmationData, setConfirmationData] = useState<ConfirmationData>(
    defaultConfirmationData
  )
  const [showModal, setShowModal] = useState(false)
  const flowContainerRef = useRef(null)
  const router = useRouter()
  const [lookupData] = useLookupData([
    KEYS['us-citizen-photo-ids'],
    KEYS['non-us-born-citizenship-immigration-documents'],
    KEYS['non-us-citizen-photo-ids'],
    KEYS['us-born-citizenship-immigration-documents'],
  ])

  const lookupMap = Object.values(lookupData).reduce((acc, el) => {
    el.forEach((x) => {
      acc[x.value] = x.label
    })
    return acc
  }, {} as any)

  useEffect(() => {
    if (state.id) {
      const {
        id: enrollmentId,
        applicationId,
        documentCheck: {
          document: documentAcronym,
          photoId: photoIdAcronym,
          namesOnDocumentsMatch,
        } = {},
        biographicInfo: { dateOfBirth, firstName, middleName, lastName },
        contact,
        finalized: { enrollmentLocationId },
      } = state
      setConfirmationData({
        enrollmentId,
        applicationId,
        documentAcronym,
        photoIdAcronym,
        enrollmentLocationId,
        namesOnDocumentsMatch,
        email: contact?.email,
        dateOfBirth,
        firstName,
        middleName,
        lastName,
      })
      clearState()
    }
  }, [clearState, state])

  useEffect(() => {
    if (
      confirmationData.dateOfBirth &&
      ageConfirmed(confirmationData.dateOfBirth, 18) &&
      !showModal
    ) {
      const timeout = setTimeout(() => {
        setShowModal(true)
      }, 2000)
      return () => clearTimeout(timeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmationData])

  useEffect(() => {
    const uid = confirmationData.enrollmentLocationId.toLowerCase()
    const chosenLocation: LocationWithLanes =
      enrollmentLocations?.find(
        (location) => location.uid.toLowerCase() === uid
      ) || ({} as unknown as LocationWithLanes)
    setChosenLocation(chosenLocation)
  }, [confirmationData.enrollmentLocationId, enrollmentLocations])

  const address = `${chosenLocation?.address1} ${
    chosenLocation?.address2 || ''
  } ${chosenLocation?.city}, ${chosenLocation?.state}`

  const serviceTerritoryId = chosenLocation?.serviceTerritoryId

  const documentName = lookupMap[confirmationData.documentAcronym] || ''
  const photoIdName = lookupMap[confirmationData.photoIdAcronym]

  const locationNameForScheduler = `${chosenLocation?.name} - ${address}`
  const enrollmentId = confirmationData.enrollmentId

  useEffect(() => {
    const inputVariables = [
      {
        name: 'enrollmentId',
        type: 'String',
        value: enrollmentId,
      },
      {
        name: 'territoryId',
        type: 'String',
        value: serviceTerritoryId,
      },
    ]
    if (serviceTerritoryId && enrollmentId) {
      const script = document.createElement('script')
      script.src = process.env.NEXT_PUBLIC_SF_LIGHTNING_URL
      script.async = true
      script.onload = () => {
        window.$Lightning.use(
          'runtime_appointmentbooking:lightningOutGuest',
          function () {
            window.$Lightning.createComponent(
              'lightning:flow',
              {},
              flowContainerRef.current,
              function (component) {
                component.startFlow(
                  process.env.NEXT_PUBLIC_SF_FLOW_NAME,
                  inputVariables
                )
                setIsFlowLoaded(true)
              }
            )
          },
          process.env.NEXT_PUBLIC_SF_ORG_BASE_URL
        )
      }
      const styleTag = document.createElement('style')
      styleTag.textContent = `.slds-visual-picker_vertical .slds-visual-picker__figure {
          height: inherit !important;}
          .runtime_appointmentbookingFlowLocation .slds-visual-picker_vertical {
          display: inline-flex !important;
          }`
      document.head.appendChild(styleTag)
      document.body.appendChild(script)
      const currentFlowContainerRef = flowContainerRef.current
      return () => {
        preloadedFlowContainer = currentFlowContainerRef
      }
    }
  }, [
    enrollmentId,
    serviceTerritoryId,
    locationNameForScheduler,
    confirmationData.enrollmentLocationId,
  ])

  const applicantName = confirmationData.middleName
    ? `${confirmationData.firstName} ${confirmationData.middleName} ${confirmationData.lastName}`
    : `${confirmationData.firstName} ${confirmationData.lastName}`

  return (
    <>
      <Stepper current={6} steps={enrollSteps} />
      <PageContent width="800px">
        <UserDataTracker />
        <StepTitle mb={6}>
          One final step to complete your TSA PreCheck<sup>®</sup> enrollment!
        </StepTitle>
        <Alert
          mb={6}
          defaultIcon
          text="Starting May 7, 2025, please ensure you have your REAL ID or other Acceptable Form of ID (for example, a passport) prior to traveling or you may encounter delays at the (airport) checkpoint."
        />
        <Text as="p" variant="body" textAlign="left" my={6}>
          Please visit any TSA PreCheck<sup>®</sup> through CLEAR enrollment
          location with your required documents to complete your application.
          For the best experience, we encourage you to book an appointment but
          we also accept walk-ins.
          <br />
          <br />
          Once completed, your application will be submitted for review. If
          approved, most applications receive their Known Traveler number (KTN)
          in 3-5 days (review periods can take up to 60 days).
        </Text>
        <Text as="p" variant="body" textAlign="left" mb={6}>
          If you are a Gold Star family member or military spouse, bring your
          TAPS letter or unexpired U.S. Military Dependent’s Card to your
          in-person enrollment for a discount.
        </Text>
        <Text
          as="p"
          variant="body"
          textAlign="left"
          mb={6}
          fontFamily="Inter-SemiBold"
        >
          Starting August 15th, 2025, the price of TSA PreCheck<sup>®</sup>{' '}
          through CLEAR will be $79.95.
        </Text>
        {serviceTerritoryId ? (
          <>
            <Button
              text="Book Your Appointment (Optional)"
              data-cy="book-your-appointment-button"
              mb={6}
              width="full"
              disabled={!isFlowLoaded}
              onClick={() =>
                router.push(
                  {
                    pathname: '/scheduler',
                    query: {
                      locationNameForScheduler: locationNameForScheduler,
                    },
                  },
                  '/scheduler'
                )
              }
            />
          </>
        ) : null}

        <Flex
          flexDirection="column"
          width="full"
          justifyContent="space-between"
          mb={6}
        >
          <Text fontFamily="Inter-SemiBold">Applicant Name</Text>
          <Text mb={3}>{applicantName}</Text>
          <Text fontFamily="Inter-SemiBold">What to bring</Text>
          <Text>{photoIdName},</Text>
          {documentName && <Text>{documentName}, </Text>}
          <Text mb={3}>Application ID</Text>
          <Text fontFamily="Inter-SemiBold">CLEAR Application ID</Text>
          <Text mb={3}>{confirmationData.applicationId}</Text>
        </Flex>

        <Text fontFamily="Inter-SemiBold" mb={5}>
          Travel is about to get so much easier.
        </Text>
        <Flex
          flexDirection={['column', 'row']}
          my={5}
          justifyContent="space-between"
          width="100%"
        >
          <FeatureCard
            icon={<img src="/feature1.svg" alt="feature1" />}
            fontWeight={0}
            borderWidth="2px"
            borderColor="trueBlue"
            borderRadius="8px"
            description="No need to remove shoes, laptops, 3-1-1 liquids, belts, or light jackets"
          />
          <FeatureCard
            icon={<img src="/feature2.svg" alt="feature2" />}
            fontWeight={0}
            borderColor="trueBlue"
            borderWidth="2px"
            borderRadius="8px"
            description="Kids 17 and under join for free when booked on the same reservation"
          />
          <FeatureCard
            icon={<img src="/feature3.svg" alt="feature3" />}
            fontWeight={0}
            borderColor="trueBlue"
            borderWidth="2px"
            borderRadius="8px"
            description="Available with 90+ airlines at 200+ airports nationwide"
          />
        </Flex>
        <OptInModal
          setShowModal={(e) => setShowModal(e)}
          showModal={showModal}
          email={confirmationData.email}
          enrollmentId={confirmationData.enrollmentId}
          setIsLoading={setIsLoading}
        />
        {isLoading && <Spinner />}
        <div
          ref={flowContainerRef}
          id="lexcontainer"
          style={{ display: 'none' }}
        />
      </PageContent>
    </>
  )
}

export default CompletePage
